import { dodajDni, isoDnia, terminyKlienta, zIso } from '../klienci/kalendarz'
import { kwota, statystykiEtapow } from '../klienci/handel'
import { zadaniaEtapu } from '../klienci/przeplyw'
import { znajdzEtap } from '../../config/klienci'

/**
 * Pulpit firmy `/pulpit` (decyzje właściciela 2026-10-11): strona startowa konta firmowego po zalogowaniu.
 * Wszystko liczymy z listy klientów (`GET /klienci`, skrót z `accounts/api.py` `_klient_skrot`) i powiadomień
 * (`GET /powiadomienia`), bez osobnego endpointu pulpitu. Tu same czyste funkcje; widoki w `components/pulpit/`.
 */

export const DNI_KALENDARZA = 7
export const DNI_POGODY = 5
export const DNI_SPRZETU = 14
export const DNI_PRZYPOMNIENIA = 7 // tak samo jak `powiadomienia.DNI_BEZ_KONTAKTU` w backendzie
export const DNI_ARCHIWUM = 21 //    i `powiadomienia.DNI_ARCHIWUM_OFERTY`
/** Pasek „Zaimportuj klientów” na pulpicie, dopóki na liście jest mniej klientów niż tyle (2026-10-13). */
export const MALO_KLIENTOW = 5
export const DNI_MONTAZU_Z_IMPORTU = 30

const dniMiedzy = (odIso, doIso) => Math.round((zIso(doIso) - zIso(odIso)) / 864e5)
const dzienZmiany = (k) => (k.updated_at ? isoDnia(new Date(k.updated_at)) : null)

export const ofertaBezKlienta = (k) => Boolean(k?.tymczasowy)
export const klienciWlasciwi = (klienci) => (klienci || []).filter((k) => !ofertaBezKlienta(k))

/** Terminy, które się odbędą albo odbyły (bez przeniesionych i odwołanych), z danymi klienta. */
const terminyFirmy = (klienci) => klienciWlasciwi(klienci).flatMap((k) => terminyKlienta(k)
  .filter((t) => t.od && (t.status === 'zaplanowany' || t.status === 'zakonczony'))
  .map((t) => ({ ...t, klientId: k.id, klient: k.nazwa || 'Klient bez nazwy', wlasciciel: k.wlasciciel || null,
    miejsce: t.miejscowosc || k.adres?.miejscowosc || k.miejscowosc || '' })))

/**
 * Najbliższe dni od dziś: dla każdego dnia terminy (montaż na kilka dni w każdym dniu z numerem „1/2”),
 * po godzinie, terminy bez godziny na końcu.
 */
export const najblizszeDni = (klienci, dzis, ile = DNI_KALENDARZA) => {
  const terminy = terminyFirmy(klienci)
  return Array.from({ length: ile }, (_, i) => {
    const iso = dodajDni(dzis, i)
    const d = zIso(iso)
    const dnia = terminy.filter((t) => t.od <= iso && (t.do || t.od) >= iso).map((t) => {
      const wszystkich = dniMiedzy(t.od, t.do || t.od) + 1
      return { ...t, czesc: wszystkich > 1 ? `${dniMiedzy(t.od, iso) + 1}/${wszystkich}` : '' }
    }).sort((a, b) => (a.godzOd || '99').localeCompare(b.godzOd || '99'))
    return { iso, dzis: i === 0, weekend: d.getDay() === 0 || d.getDay() === 6, terminy: dnia }
  })
}

/**
 * Najwyżej `maks` terminów w kratce dnia (uwaga właściciela 2026-10-12: kilka wydarzeń dziennie nie może
 * rozciągać tygodnia w dół), reszta jako „+N”. Pierwszeństwo mają terminy jeszcze nie zakończone; pokazane
 * zostają w kolejności godzin z `najblizszeDni`.
 */
export const MAKS_TERMINOW_DNIA = 2
export const skrotDnia = (terminy, maks = MAKS_TERMINOW_DNIA) => {
  const wybrane = new Set([...terminy].sort((a, b) => Number(a.status === 'zakonczony') - Number(b.status === 'zakonczony')).slice(0, maks))
  return { widoczne: terminy.filter((t) => wybrane.has(t)), ukrytych: Math.max(0, terminy.length - maks) }
}

// ── Kolejne kroki ────────────────────────────────────────────────────────────

// Id terminu montażu, który zakłada import klientów (backend `accounts/import_klientow.py` `_termin_montazu`).
const ID_MONTAZU_Z_IMPORTU = 'import-montaz'

/**
 * Klient wgrany importem jako dawno zamontowany i od importu nietknięty (2026-10-13). Import kładzie zamontowanych
 * na etap zakończonego montażu, a reguły „Co dalej” proponują im od razu formalności; kilkuset archiwalnych klientów
 * z arkusza zasypałoby „Kolejne kroki”. Takiemu klientowi pulpit nie proponuje następnego etapu, dopóki ktoś nie
 * zmieni karty albo etapu (`updated_at` najwyżej 2 s po utworzeniu, jak `ZAPAS_EDYCJI` w backendzie). Montaż
 * z ostatnich `DNI_MONTAZU_Z_IMPORTU` dni zostaje w zwykłych regułach: tam formalności zwykle jeszcze trwają.
 */
export const archiwalnyZImportu = (k, dzis) => {
  const t = terminyKlienta(k).find((x) => x.id === ID_MONTAZU_Z_IMPORTU)
  if (!t || t.status !== 'zakonczony' || dniMiedzy(t.do || t.od, dzis) < DNI_MONTAZU_Z_IMPORTU) return false
  const utworzony = Date.parse(k.created_at)
  const zmieniony = Date.parse(k.updated_at)
  return Number.isFinite(utworzony) && Number.isFinite(zmieniony) && zmieniony - utworzony <= 2000
}

/** Fakty reguł „Co dalej” (`lib/klienci/przeplyw`) z samego skrótu klienta, bez otwierania karty. */
export const faktyZeSkrotu = (k) => {
  const terminy = terminyKlienta(k)
  const jest = (rodzaj, statusy) => terminy.some((t) => t.rodzaj === rodzaj && statusy.includes(t.status))
  const s = k.sprzet || {}
  return {
    spotkanieZakonczone: jest('spotkanie', ['zakonczony']),
    spotkanieUmowione: jest('spotkanie', ['zaplanowany', 'zakonczony']),
    montazUmowiony: jest('montaz', ['zaplanowany', 'zakonczony']),
    montazZakonczony: jest('montaz', ['zakonczony']),
    zestaw: (k.zestawy || 0) > 0,
    cena: k.cena?.brutto != null || k.cena?.netto != null,
    umowa: Boolean(k.umowa),
    oferta: Boolean(k.oferta?.numer),
    osdWyslane: Boolean(k.zgloszenie_osd),
    sprzetZamowiony: s.wszystkich > 0 ? s.zamowione >= s.wszystkich : Boolean(s.zamowiony),
    sprzetDojechal: s.wszystkich > 0 ? s.dojechalo >= s.wszystkich : Boolean(s.dojechal),
    teczka: Boolean(k.teczka_pme2_id),
    osd: true,
    pme2: true,
  }
}

// Zadanie „Co dalej” na pulpicie: krótko, w trybie rozkazującym, z przyciskiem do miejsca w karcie.
const ZADANIA = {
  spotkanie: (f) => (f.spotkanieUmowione ? ['potwierdź, że spotkanie się odbyło', 'Terminy', 'terminy'] : ['umów spotkanie', 'Zaplanuj', 'terminy']),
  zestaw: () => ['dobierz zestaw', 'Zestaw', 'zestaw'],
  cena: () => ['ustal cenę dla klienta', 'Cena', 'cena'],
  oferta: () => ['przygotuj ofertę', 'Oferta', 'oferta'],
  umowa: () => ['czeka na podpis umowy', 'Karta', ''],
  zamowiony: () => ['zamów sprzęt', 'Zestaw', 'zestaw'],
  dojechal: () => ['sprzęt jeszcze nie dojechał', 'Zestaw', 'zestaw'],
  montaz: () => ['umów montaż', 'Zaplanuj', 'terminy'],
  montazKoniec: () => ['potwierdź zakończenie montażu', 'Terminy', 'terminy'],
  osdWyslane: () => ['wyślij zgłoszenie do operatora', 'Karta', ''],
  teczka: () => ['załóż teczkę PME2', 'Karta', ''],
}

// Powiadomienia z dzwonka w tym samym kształcie; mają pierwszeństwo, bo dotyczą terminów.
const Z_POWIADOMIEN = {
  montaz_braki: ['uzupełnij dane przed montażem', 'Uzupełnij', ''],
  montaz_odbyl: ['potwierdź montaż', 'Terminy', 'terminy'],
  spotkanie_odbylo: ['czy spotkanie się odbyło?', 'Terminy', 'terminy'],
  spotkanie_wkrotce: ['spotkanie wkrótce', 'Karta', 'terminy'],
  klient_bez_kontaktu: ['oferta bez odpowiedzi, zadzwoń', 'Karta', ''],
}

const PILNOSC = { pilne: 0, wkrotce: 1, zwykle: 2 }

const opisTerminu = (iso, dzis) => {
  const r = dniMiedzy(dzis, iso)
  if (r === 0) return 'dziś'
  if (r === 1) return 'jutro'
  if (r === -1) return 'wczoraj'
  if (r > 1 && r < 7) return `za ${r} dni`
  return zIso(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' })
}

/** Najbliższy zaplanowany montaż klienta od dziś (iso) albo null. */
const najblizszyMontaz = (k, dzis) => terminyKlienta(k)
  .filter((t) => t.rodzaj === 'montaz' && t.status === 'zaplanowany' && (t.do || t.od) >= dzis)
  .sort((a, b) => a.od.localeCompare(b.od))[0]?.od || null

/**
 * Jedna lista „co teraz zrobić”, najwyżej jedna pozycja na klienta: najpierw powiadomienia (terminy i braki),
 * potem pierwsze niespełnione zadanie etapu z „Co dalej”, a gdy wszystkie spełnione, propozycja następnego etapu.
 * Kolejność: pilne, na dniach, zwykłe; w grupie od najbliższej daty.
 */
export const kolejneKroki = ({ klienci = [], etapy = [], powiadomienia = [], dzis }) => {
  const wlasciwi = klienciWlasciwi(klienci)
  const poId = new Map(wlasciwi.map((k) => [k.id, k]))
  const wynik = new Map()
  powiadomienia.forEach((p) => {
    const k = poId.get(p.klient_id)
    const wzor = Z_POWIADOMIEN[p.typ]
    if (!k || !wzor || wynik.has(k.id)) return
    const [zadanie, akcja, sekcja] = wzor
    const etap = znajdzEtap(etapy, k.status)
    wynik.set(k.id, {
      klucz: p.klucz, klientId: k.id, klient: k.nazwa, etap, zadanie,
      opis: p.typ === 'montaz_braki' ? p.tresc.replace(/\.$/, '') : p.typ === 'spotkanie_wkrotce' ? p.tresc.replace(/\.$/, '') : `termin ${opisTerminu(p.data, dzis)}`,
      pilnosc: p.pilne ? 'pilne' : (p.typ === 'spotkanie_wkrotce' || p.typ === 'montaz_braki' ? 'wkrotce' : 'zwykle'),
      data: p.data, akcja, sekcja, wlasciciel: k.wlasciciel || null,
    })
  })
  wlasciwi.forEach((k) => {
    if (wynik.has(k.id)) return
    const etap = znajdzEtap(etapy, k.status)
    const f = faktyZeSkrotu(k)
    const zadania = zadaniaEtapu(etap.krok, f)
    if (!zadania) return
    const montaz = najblizszyMontaz(k, dzis)
    const zmiana = dzienZmiany(k)
    const cisza = zmiana ? dniMiedzy(zmiana, dzis) : 0
    const bazowa = {
      klientId: k.id, klient: k.nazwa, etap, wlasciciel: k.wlasciciel || null,
      pilnosc: montaz && dniMiedzy(dzis, montaz) <= 7 ? 'wkrotce' : 'zwykle',
      data: montaz || zmiana || dzis,
      opis: montaz ? `montaż ${opisTerminu(montaz, dzis)}` : cisza >= 7 ? `bez zmian od ${cisza} dni` : '',
    }
    const brak = zadania.find((z) => z.blokuje && !z.spelnione)
    if (brak) {
      const [zadanie, akcja, sekcja] = (ZADANIA[brak.id] || (() => [brak.tekst, 'Karta', '']))(f)
      wynik.set(k.id, { ...bazowa, klucz: `${k.id}:${brak.id}`, zadanie, akcja, sekcja })
      return
    }
    const i = etapy.findIndex((e) => e.id === etap.id)
    const nastepny = i >= 0 ? etapy[i + 1] : null
    if (nastepny && !archiwalnyZImportu(k, dzis)) {
      wynik.set(k.id, { ...bazowa, klucz: `${k.id}:etap`, zadanie: `gotowy do etapu „${nastepny.nazwa}”`, akcja: 'Przenieś', sekcja: '' })
    }
  })
  return [...wynik.values()].sort((a, b) => (PILNOSC[a.pilnosc] - PILNOSC[b.pilnosc]) || String(a.data).localeCompare(String(b.data)))
}

// ── Oferty bez klienta ───────────────────────────────────────────────────────

/** Oferty bez klienta: aktywne (z dniami ciszy i czy przypomnieć) i liczba w archiwum (ręcznie albo po 21 dniach). */
export const ofertyBezKlienta = (klienci, dzis) => {
  const wszystkie = (klienci || []).filter(ofertaBezKlienta).map((k) => {
    const zmiana = dzienZmiany(k) || dzis
    const cisza = dniMiedzy(zmiana, dzis)
    return { ...k, cisza, archiwum: Boolean(k.tymczasowy.archiwum) || cisza >= DNI_ARCHIWUM, przypomnij: cisza >= DNI_PRZYPOMNIENIA }
  })
  const aktywne = wszystkie.filter((o) => !o.archiwum).sort((a, b) => b.cisza - a.cisza)
  const miesiac = dzis.slice(0, 7)
  const zMiesiaca = (iso) => (iso || '').slice(0, 7) === miesiac
  return {
    aktywne,
    archiwum: wszystkie.filter((o) => o.archiwum),
    wMiesiacu: wszystkie.filter((o) => zMiesiaca(o.tymczasowy.od)).length,
  }
}

// ── Sprzedaż w toku ──────────────────────────────────────────────────────────

const narzutKlienta = (k) => kwota(k.cena?.narzut) || 0
const sumaNarzutu = (lista) => lista.reduce((s, k) => s + narzutKlienta(k), 0)

/**
 * Etapy z liczbą klientów, kwotą brutto i narzutem oraz montaże w bieżącym miesiącu (liczba, wartość, narzut).
 * Narzut to `cena.narzut` ze skrótu klienta (netto, z `dane.cena.skladniki.narzut`): konto główne widzi kwoty i narzut,
 * członek zespołu sam narzut (decyzja właściciela 2026-10-11).
 */
export const sprzedaz = (klienci, etapy, dzis) => {
  const wlasciwi = klienciWlasciwi(klienci)
  const st = statystykiEtapow(wlasciwi, etapy)
  const wiersze = st.etapy.map((w) => ({ ...w, narzut: sumaNarzutu(wlasciwi.filter((k) => (w.id ? k.status === w.id : !etapy.some((e) => e.id === k.status)))) }))
  const razem = { ...st.razem, narzut: sumaNarzutu(wlasciwi) }
  const miesiac = dzis.slice(0, 7)
  const zMontazem = wlasciwi.filter((k) => terminyKlienta(k).some((t) => t.rodzaj === 'montaz'
    && (t.status === 'zaplanowany' || t.status === 'zakonczony') && t.od.slice(0, 7) === miesiac))
  return {
    etapy: wiersze.filter((w) => w.liczba > 0),
    razem,
    montaze: { liczba: zMontazem.length, brutto: zMontazem.reduce((s, k) => s + (kwota(k.cena?.brutto) || 0), 0), narzut: sumaNarzutu(zMontazem) },
  }
}

// ── Montaże: mapa, pogoda, sprzęt ────────────────────────────────────────────

/**
 * Tekst adresu do wyszukiwarki GUGiK. Usługa chce najpierw miejscowość, potem ulicę („32-050 Skawina, Mickiewicza 12”;
 * „Mickiewicza 12, Skawina” nic nie znajduje, sprawdzone 2026-10-11). Wieś bez ulic: „Liszki 120”.
 */
export const tekstAdresu = ({ ulica, nrDomu, kod, miejscowosc } = {}) => {
  if (!miejscowosc) return ''
  const miasto = [kod, miejscowosc].filter(Boolean).join(' ')
  if (!ulica || ulica === miejscowosc) return [miasto, nrDomu].filter(Boolean).join(' ')
  return `${miasto}, ${[ulica, nrDomu].filter(Boolean).join(' ')}`
}

/**
 * Montaże do mapy: na klienta ostatni zakończony albo najbliższy zaplanowany montaż. Adres z karty, a gdy pusty,
 * z terminu. `zrobiony` = zakończony; zaplanowany z przeszłości (niepotwierdzony) też jest „zaplanowany”.
 */
export const montazeDoMapy = (klienci) => klienciWlasciwi(klienci).map((k) => {
  const montaze = terminyKlienta(k).filter((t) => t.rodzaj === 'montaz' && (t.status === 'zaplanowany' || t.status === 'zakonczony'))
  if (!montaze.length) return null
  const zaplanowany = montaze.filter((t) => t.status === 'zaplanowany').sort((a, b) => a.od.localeCompare(b.od))[0]
  const t = zaplanowany || montaze.sort((a, b) => b.od.localeCompare(a.od))[0]
  // Termin ma ulicę razem z numerem („Kolejowa 3”), karta osobno.
  const adres = k.adres?.miejscowosc ? k.adres : { ulica: t.ulica, miejscowosc: t.miejscowosc || k.miejscowosc }
  return {
    klientId: k.id, klient: k.nazwa, wlasciciel: k.wlasciciel || null, zrobiony: !zaplanowany,
    od: t.od, do: t.do || t.od, kwp: k.moc_pv?.wartosc ?? null,
    miejscowosc: adres.miejscowosc || '', adres: tekstAdresu(adres), wsp: k.adres?.wsp || null,
  }
}).filter((m) => m && (m.adres || m.wsp))

/**
 * Miejscowość do dopisania po nazwie klienta albo ''. Domyślna nazwa karty (i każdego klienta z importu) to już
 * „Jan Kowalski, Zgierz” (`nazwaKlienta`), więc bez tego pulpit pokazywał „Jan Kowalski, Zgierz, Zgierz” (2026-10-13).
 */
export const miejscowoscPoNazwie = (nazwa, miejscowosc) => {
  const m = String(miejscowosc || '').trim()
  return m && !String(nazwa || '').toLowerCase().includes(m.toLowerCase()) ? m : ''
}

/** Zaplanowane montaże w oknie [dziś, dziś + dni), od najbliższego (do pogody i sprzętu). */
export const montazeWOknie = (klienci, dzis, dni) => {
  const koniec = dodajDni(dzis, dni)
  return montazeDoMapy(klienci).filter((m) => !m.zrobiony && m.do >= dzis && m.od < koniec).sort((a, b) => a.od.localeCompare(b.od))
}

/**
 * Ostrzeżenie pogodowe na pracę na dachu: burza i śnieg zawsze, deszcz od 2 mm, wiatr od 40 km/h.
 * Zwraca krótki tekst albo ''.
 */
export const ostrzezeniePogody = (d) => {
  if (!d) return ''
  if (d.symbol === 'burza') return 'burza'
  if (d.symbol === 'snieg') return 'śnieg'
  const czesci = []
  if (d.opad_mm >= 2) czesci.push(`deszcz ${String(d.opad_mm).replace('.', ',')} mm`)
  if (d.wiatr_kmh >= 40) czesci.push(`wiatr ${d.wiatr_kmh} km/h`)
  return czesci.join(', ')
}

/**
 * Adres strony modelu pozycji zestawu (`model_url`, np. `/model/panel/…`) do ikony przy sprzęcie na montaże
 * (2026-10-12). Tylko ścieżki wewnątrz serwisu: starsze pozycje bywają z adresem sklepu, a ta sekcja jest bez
 * sklepów. Brak adresu albo adres zewnętrzny to null, czyli bez ikony.
 */
export const adresModelu = (pozycja) => {
  const url = typeof pozycja?.model_url === 'string' ? pozycja.model_url.trim() : ''
  return url.startsWith('/') && !url.startsWith('//') ? url : null
}
